import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import Space from '@/components/Space';
import useBoopMinimal from '@/hooks/use-boop-minimal';

const STRETCH_AMOUNT = 3;

function StretchyStateVsActionDemo() {
  return (
    <Row>
      <Col>
        <Heading>State-based</Heading>
        <Arrow boop={false} />
      </Col>
      <Col>
        <Heading>Event-based</Heading>
        <Arrow boop={true} />
      </Col>
    </Row>
  );
}

function Arrow({ boop }: { boop: boolean }) {
  const [isHovering, setIsHovering] = React.useState(false);
  const isBooped = useBoopMinimal(isHovering);

  const shouldUseStretchyVariant = boop ? isBooped : isHovering;

  const lineProps = {
    x2: LINE_EXTENDS_TO[
      shouldUseStretchyVariant ? 'booped' : 'default'
    ],
  };
  const polyLineProps = {
    points: POINTS[shouldUseStretchyVariant ? 'squeezed' : 'default'],
  };

  const transition = { type: 'spring', stiffness: 300, damping: 12 };

  return (
    <ArrowBtn
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTouchStart={() => setIsHovering(true)}
      onTouchEnd={() => setIsHovering(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          setIsHovering(true);
        }
      }}
      onKeyUp={(event) => {
        if (event.key === 'Enter') {
          setIsHovering(false);
        }
      }}
    >
      <Svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <motion.line
          x1="5"
          y1="12"
          y2="12"
          animate={lineProps}
          transition={transition}
        />
        <motion.polyline
          animate={polyLineProps}
          transition={transition}
        />
      </Svg>
    </ArrowBtn>
  );
}

const POINTS = {
  default: '12 5 19 12 12 19',
  squeezed: `${12 + STRETCH_AMOUNT} 7 ${19 + STRETCH_AMOUNT} 12 ${
    12 + STRETCH_AMOUNT
  } 17`,
  regular: `${12 + STRETCH_AMOUNT} 5 ${19 + STRETCH_AMOUNT} 12 ${
    12 + STRETCH_AMOUNT
  } 19`,
};
const LINE_EXTENDS_TO = {
  default: 18,
  booped: 18 + STRETCH_AMOUNT,
};

const Row = styled(Space)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 32px;
  gap: 32px;
  border: var(--page-border);
  border-radius: 8px;
  margin-bottom: 64px;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Heading = styled.p`
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  text-align: center;
  margin-bottom: 0;
`;

const ArrowBtn = styled.button`
  padding: 12px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const Svg = styled.svg`
  display: block;
  transition: opacity 250ms;
  width: 128px;
  height: 128px;
  overflow: visible;
  fill: none;
  stroke: white;
  stroke-width: 2px;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

export default StretchyStateVsActionDemo;
